import { AssetState } from '@/features/remotion-editor/editor/assets/assets';
import { SetState } from '@/features/remotion-editor/editor/context-provider';
import { logEditorStateDebounced } from '@/features/remotion-editor/editor/debuging-console/editor-state-console';
import {
  AudioItem,
  AudioItemRenderMeta,
  isAudioItem,
} from '@/features/remotion-editor/editor/items/audio/audio-item-type';
import {
  CaptionsItem,
  isCaptionsItem,
} from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import {
  EditorState,
  TrackType,
} from '@/features/remotion-editor/editor/state/types';
import {
  AudioClipInfo,
  CaptionClipInfo,
  ClipInfo,
  isAudioClip,
  isCaptionClip,
} from '@/shared/apis/editor/clip-types';
import { omit } from 'lodash';
import {
  ContentInfoMap,
  createAssetStatus,
  createAudioItemFromClip,
  createAudioRenderMeta,
  createCaptionItemFromClip,
  createCaptionRenderMeta,
  normalizeContentInfos,
} from '@/features/remotion-editor/editor/state/hydrate/hydrate-editor-state-utils';
import { TextStyleConfig } from '@/features/remotion-editor/editor/state/hydrate/hydrate-editor-state';
import { convertFrameToMs } from '@/features/remotion-editor/editor/utils/convert-frame-to-ms';
import {
  isVideoItem,
  VideoItem,
} from '@/features/remotion-editor/editor/items/video/video-item-type';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import {
  ImageItem,
  isImageItem,
} from '@/features/remotion-editor/editor/items/image/image-item-type';
import { EditorDataResponse } from '@/shared/apis/scriptSnippetApi';
import { findOrphanedTransitions } from '@/features/remotion-editor/editor/utils/find-linked-items';
import {
  collectCurrentSceneItemsFromMainTracks,
  rebuildTracksWithNewItemsOnMainTracks,
} from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/script/utils/regenerate-caption-audiov2-track-policy';

interface RegenerateCaptionAudioV2Props {
  tts: ClipInfo[];
  editorData: EditorDataResponse;
  editorState: EditorState;
  sceneId: number | null;
  setState: SetState;
  projectId: number;
  sceneVideoEnd?: number;
}

/**
 * TTS 재생성 응답을 현재 scene에 반영하는 메인 진입점이다.
 * scene 범위 계산, 기존 item 제거, regenerated audio/caption/video 생성,
 * 그리고 이후 item shift까지 한 번에 처리한다.
 */
export const regenerateCaptionAudioV2 = async ({
  tts,
  editorData,
  editorState,
  sceneId,
  setState,
  projectId,
  sceneVideoEnd,
}: RegenerateCaptionAudioV2Props) => {
  if (sceneId == null) {
    console.error('regenerateCaptionAudioV2 stop: sceneId null');
    return;
  }
  const normalizedContentInfos = normalizeContentInfos(editorData);

  const contentInfoMap: ContentInfoMap = normalizedContentInfos.reduce(
    (acc: ContentInfoMap, info: ContentInfoMap[number]) => {
      if (info?.id != null) {
        acc[info.id] = info;
      }
      return acc;
    },
    {},
  );

  const { undoableState } = editorState;
  const { tracks, items, assets, fps, compositionWidth, compositionHeight } =
    undoableState;

  // fps가 0 이하인 경우 안전한 기본값으로 교체
  const safeFps = fps > 0 ? fps : 30;

  logEditorStateDebounced({ tracks, items, assets });

  // scene card가 main track 기준으로 만들어지므로 재생성 대상 수집도 같은 정책으로 맞춘다.
  const { currentSceneAudioItems, currentSceneCaptionItems } =
    collectCurrentSceneItemsFromMainTracks(tracks, items, sceneId);

  if (
    currentSceneAudioItems.length === 0 &&
    currentSceneCaptionItems.length === 0
  ) {
    console.error('regenerateCaptionAudioV2 stop: no scene items');
    return;
  }

  // TTS 응답을 오디오/캡션으로 분리
  const { ttsAudioResponseClips, ttsCaptionResponseClips } =
    splitTtsAudioAndCaptionClips(tts);

  if (
    ttsAudioResponseClips.length === 0 &&
    ttsCaptionResponseClips.length === 0
  ) {
    console.error(
      'regenerateCaptionAudioV2 stop: no audio/caption clips in response',
    );
    return;
  }

  const { startCaptionItem: originalStartCaptionItem } =
    getSceneCaptionFromRange(currentSceneCaptionItems);

  const sceneStartFrame =
    originalStartCaptionItem?.from ?? currentSceneAudioItems?.[0]?.from ?? 0;
  const sceneStartMs = convertFrameToMs(sceneStartFrame, safeFps);

  // 기존 씬의 시작/끝 범위(ms) 계산
  const { oldSceneStartMs, oldSceneEndMs } = getSceneRangeMs({
    currentSceneAudioItems,
    currentSceneCaptionItems,
    sceneVideoEnd,
    fps: safeFps,
  });

  // 현재 씬 오디오/캡션의 item id 모음
  const currentSceneItemIds = getCurrentSceneIds(
    [...currentSceneAudioItems, ...currentSceneCaptionItems],
    'id',
  );

  // 현재 씬 오디오/캡션의 asset id 모음
  const currentSceneAssetsIds = getCurrentSceneIds(
    [...currentSceneAudioItems, ...currentSceneCaptionItems],
    'assetId',
  );

  const newTracks = structuredClone(tracks);
  const newItems = structuredClone(items);
  const newAssets = structuredClone(assets);
  const newAssetStatus = structuredClone(editorState.assetStatus);

  // 기존 linkId와 충돌하지 않도록 하기 위한 기존 linkId를 제외한 새로운 linkIds
  const existingLinkIds = getExistingItemLinkIds(Object.values(items), [
    ...currentSceneAudioItems,
    ...currentSceneCaptionItems,
  ]);

  const getNextClipId = createClipIdGenerator();
  const getNextLinkId = createLinkIdGenerator(existingLinkIds);

  // 오디오/캡션 클립을 연속 구간으로 정규화
  const { normalizedAudioClips, normalizedCaptionClips } =
    normalizeAudioAndCaptionClipsSequentially({
      ttsAudioClips: ttsAudioResponseClips,
      ttsCaptionClips: ttsCaptionResponseClips,
      originalStartCaptionItem,
      sceneId,
      fps: safeFps,
      getNextClipId,
      getNextLinkId,
    });

  if (normalizedAudioClips.length === 0) {
    console.error('regenerateCaptionAudioV2 stop: normalized audio empty');
    return;
  }

  const newSceneStartMs = normalizedAudioClips[0]?.videoStart ?? sceneStartMs;
  const newSceneEndMs =
    normalizedAudioClips[normalizedAudioClips.length - 1]?.videoEnd ??
    newSceneStartMs;

  // 기존 씬 범위와 겹치는 비디오/이미지만 제거 대상으로 선정
  const sceneVisualItems = findVisualItemsInRange({
    items: newItems,
    rangeStartMs: oldSceneStartMs,
    rangeEndMs: oldSceneEndMs,
    fps: safeFps,
  });

  const deletedItemIds = new Set<string>([
    ...currentSceneItemIds,
    ...sceneVisualItems.map((item) => item.id),
  ]);

  // 트랙에서 삭제 대상 제거
  newTracks.forEach((track) => {
    track.items = track.items.filter((itemId) => !deletedItemIds.has(itemId));
  });

  currentSceneItemIds
    .filter((itemId): itemId is string => Boolean(itemId))
    .forEach((itemId) => {
      delete newItems[itemId];
    });
  sceneVisualItems.forEach((visualItem) => {
    delete newItems[visualItem.id];
  });

  // 기존 씬 이후 아이템 시프트 대상 수집
  const shiftItemIds = collectItemsStartingAtOrAfterMs({
    tracks: newTracks,
    items: newItems,
    startMs: oldSceneEndMs,
    fps: safeFps,
  });

  // 기존 캡션 스타일을 복사해 유지 (위치/크기 제외)
  const captionStyleConfig = getCaptionStyle(originalStartCaptionItem);

  // 기존 캡션에서 TextStyleConfig 생성 (captionYRatio 포함)
  const textStyleConfig = createTextStyleConfigFromCaption(
    originalStartCaptionItem,
    compositionHeight,
  );

  // 오디오 생성
  const createAudioResult = normalizedAudioClips.map((clip) => {
    const contentInfo = clip.contentId
      ? contentInfoMap[clip.contentId]
      : undefined;

    const renderMeta: AudioItemRenderMeta | undefined = createAudioRenderMeta({
      clip,
    });
    return createAudioItemFromClip({
      clip,
      fps: safeFps,
      contentInfo,
      isMainTrack: true,
      renderMeta,
    });
  });

  // 캡션 생성 (textStyleConfig로 위치/스타일 설정 후, 추가 스타일 덮어쓰기)
  const createCaptionResult = await Promise.all(
    normalizedCaptionClips.map(async (clip) => {
      const metadata = createCaptionRenderMeta({ clip });
      const result = await createCaptionItemFromClip({
        clip,
        fps: safeFps,
        compositionWidth,
        compositionHeight,
        metadata,
        projectId,
        textStyleConfig,
      });

      if (!result) return null;

      return {
        ...result,
        item: {
          ...result.item,
          ...(captionStyleConfig ?? {}),
        },
      };
    }),
  );

  // 비디오/이미지 생성 (오디오 구간/링크에 맞춤)
  const visualItems = createVisualItemsAlignedWithAudio({
    audioClips: normalizedAudioClips,
    templateVisualItems: sceneVisualItems,
    fps: safeFps,
    getNextClipId,
    getNextLinkId,
  });

  const finalizedItems = structuredClone(newItems);
  const finalizedAssets = structuredClone(newAssets);
  const finalizedAssetStatus = structuredClone(newAssetStatus);

  // 오디오/캡션/비디오를 최종 상태에 반영
  [...createAudioResult, ...createCaptionResult].forEach((result) => {
    if (!result) return;
    const { item, asset } = result;

    finalizedItems[item.id] = item;
    finalizedAssets[asset.id] = asset;
    finalizedAssetStatus[asset.id] = createAssetStatus(asset);
  });

  visualItems.forEach((item) => {
    finalizedItems[item.id] = item;
  });

  const shiftFromMs = newSceneEndMs - oldSceneEndMs;
  const shiftFrom = Math.round((shiftFromMs / 1000) * safeFps);

  // 이후 아이템 위치/메타 시프트
  if (shiftFrom !== 0 && shiftItemIds.length > 0) {
    shiftItem({
      shiftItemIds,
      finalizedItems,
      shiftFrom,
      shiftFromMs,
    });
  }

  // 1프레임 오차 검토 및 수정 로직
  fixOneFrameOffsetBetweenSequentialAudioItems({
    items: finalizedItems,
    fps: safeFps,
  });

  // regenerated item은 main track에만 다시 꽂고, main track이 없으면 helper가 새 main track을 생성한다.
  const finalizedTracks = rebuildTracksWithNewItemsOnMainTracks({
    tracks: newTracks,
    items: finalizedItems,
    addedItemIdsByType: {
      audio: createAudioResult.map((result) => result?.item.id),
      caption: createCaptionResult.map((result) => result?.item.id),
      video: visualItems.map((item) => item.id),
    },
  });

  const existingDeletedAssetIds = new Set(
    editorState.undoableState.deletedAssets.map((d) => d.assetId),
  );

  const deletedAssetEntries: {
    remoteUrl: string | null;
    remoteFileKey: string | null;
    assetId: string;
    statusAtDeletion: AssetState;
  }[] = currentSceneAssetsIds
    .filter((assetId): assetId is string => Boolean(assetId))
    .filter((assetId) => !existingDeletedAssetIds.has(assetId))
    .map((assetId) => {
      const asset = assets[assetId];
      const status = editorState.assetStatus[assetId];
      return {
        assetId,
        remoteUrl: asset?.remoteUrl ?? null,
        remoteFileKey: asset?.remoteFileKey ?? null,
        statusAtDeletion: status ?? { type: 'uploaded' as const },
      };
    });

  // 삭제된 아이템을 참조하는 transition 찾기
  const orphanedTransitionIds = findOrphanedTransitions({
    itemIds: Array.from(deletedItemIds),
    transitions: undoableState.transitions,
  });

  setState({
    update: (prev) => {
      // 삭제된 아이템을 참조하는 transition 제거
      const newTransitions = { ...prev.undoableState.transitions };
      for (const transitionId of orphanedTransitionIds) {
        delete newTransitions[transitionId];
      }

      const nextState = {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          tracks: finalizedTracks,
          items: finalizedItems,
          assets: finalizedAssets,
          transitions: newTransitions,
          deletedAssets: [
            ...prev.undoableState.deletedAssets,
            ...deletedAssetEntries,
          ],
        },
        assetStatus: finalizedAssetStatus,
      };
      return nextState;
    },
    commitToUndoStack: true,
  });
};

/** 현재 씬 오디오/캡션의 시작/끝 범위를 ms 단위로 반환 */
const getSceneRangeMs = ({
  currentSceneAudioItems,
  currentSceneCaptionItems,
  sceneVideoEnd,
  fps,
}: {
  currentSceneAudioItems: AudioItem[];
  currentSceneCaptionItems: CaptionsItem[];
  sceneVideoEnd?: number | null;
  fps: number;
}) => {
  /** 씬 시작 후보(ms) */
  const startCandidates = [
    ...currentSceneAudioItems.map((item) => getItemStartMs({ item, fps })),
    ...currentSceneCaptionItems.map((item) => getItemStartMs({ item, fps })),
  ].filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value),
  );

  /** 씬 종료 후보(ms) */
  const endCandidates = [
    ...currentSceneAudioItems.map((item) => getItemEndMs({ item, fps })),
    ...currentSceneCaptionItems.map((item) => getItemEndMs({ item, fps })),
    sceneVideoEnd,
  ].filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value),
  );

  const oldSceneStartMs =
    startCandidates.length > 0 ? Math.min(...startCandidates) : 0;
  const oldSceneEndMs =
    endCandidates.length > 0 ? Math.max(...endCandidates) : 0;

  return { oldSceneStartMs, oldSceneEndMs };
};

/** 기존 씬 범위와 겹치는 비디오/이미지 아이템을 가져오는 함수 */
const findVisualItemsInRange = ({
  items,
  rangeStartMs,
  rangeEndMs,
  fps,
}: {
  items: Record<string, EditorStarterItem>;
  rangeStartMs: number;
  rangeEndMs: number;
  fps: number;
}): Array<VideoItem | ImageItem> => {
  /** 인접 프레임 오차 허용치 */
  const epsilonMs = convertFrameToMs(1, fps) / 2;

  const filterVideoItems = Object.values(items)
    ?.filter((item) => isVideoItem(item))
    ?.filter((item) => item?.isMainTrack === true);
  const filterImageItems = Object.values(items)
    ?.filter((item) => isImageItem(item))
    ?.filter((item) => item?.isMainTrack === true);

  return [...filterVideoItems, ...filterImageItems].filter((item) => {
    const { startMs, endMs } = getMsRangeFromItem(item, fps);
    // 끝과 시작이 맞닿는 인접 구간은 삭제 대상에서 제외한다.
    return startMs < rangeEndMs - epsilonMs && endMs > rangeStartMs + epsilonMs;
  });
};

/** 삭제된 구간 끝 이후에 시작하는 아이템 id를 수집해 시프트 대상 목록으로 반환 */
const collectItemsStartingAtOrAfterMs = ({
  tracks,
  items,
  startMs,
  fps,
}: {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  startMs: number;
  fps: number;
}) => {
  /** 삭제 구간 이후부터 시프트할 아이템 목록 */
  const shiftItemSet = new Set<string>();
  const thresholdMs = startMs - convertFrameToMs(1, fps);

  tracks.forEach((track) => {
    if (track?.metadata?.isMainTrack !== true) return;

    track.items?.forEach((itemId) => {
      const item = items[itemId];
      if (!item) return;
      if (
        (isAudioItem(item) ||
          isCaptionsItem(item) ||
          isVideoItem(item) ||
          isImageItem(item)) &&
        item.isMainTrack !== true
      )
        return;

      const { startMs: itemStartMs } = getMsRangeFromItem(item, fps);
      if (itemStartMs >= thresholdMs) {
        shiftItemSet.add(itemId);
      }
    });
  });

  return Array.from(shiftItemSet);
};

/** 오디오 클립 구간과 링크를 그대로 따라가는 비디오/이미지 아이템 생성기 */
const createVisualItemsAlignedWithAudio = ({
  audioClips,
  templateVisualItems,
  fps,
  getNextClipId,
  getNextLinkId,
}: {
  audioClips: AudioClipInfo[];
  templateVisualItems: Array<VideoItem | ImageItem>;
  fps: number;
  getNextClipId: () => number;
  getNextLinkId: () => number;
}): Array<VideoItem | ImageItem> => {
  if (!audioClips.length || !templateVisualItems.length) {
    return [];
  }

  /** 비주얼(비디오/이미지) 템플릿을 시작 프레임 기준으로 정렬 */
  const sortedTemplates = [...templateVisualItems].sort(
    (a, b) => a.from - b.from,
  );

  const createdItems = audioClips.map((clip) => {
    const startMs = clip.videoStart ?? 0;
    const endMs = clip.videoEnd ?? startMs;

    if (endMs <= startMs) return null;

    const template = pickTemplateVisualItem({
      templates: sortedTemplates,
      clipStartMs: startMs,
      fps,
    });

    if (!template) return null;

    // 오디오 구간을 프레임 단위로 변환
    const { from, durationInFrames } = msRangeToFrameRange({
      fps,
      startMs,
      endMs,
    });

    const clipId = getNextClipId();
    const linkId =
      typeof clip.linkId === 'number' && Number.isFinite(clip.linkId)
        ? clip.linkId
        : getNextLinkId();
    if (isVideoItem(template)) {
      // 템플릿 시작 프레임과의 차이로 원본 영상 내부 오프셋 계산
      const deltaFrame = from - (template.from ?? 0);

      const videoStartFromInSeconds = Math.max(
        0,
        (template.videoStartFromInSeconds ?? 0) + deltaFrame / fps,
      );

      const renderMeta = {
        clipType: CLIP_TYPES.VIDEO,
        id: clipId,
        linkId,
        start: Math.round(videoStartFromInSeconds * 1000),
        end: Math.round(videoStartFromInSeconds * 1000 + (endMs - startMs)),
        videoStart: startMs,
        videoEnd: endMs,
      } as VideoItem['renderMeta'];

      return {
        ...template,
        id: `${template.id}-${generateRandomId(6)}`,
        from,
        durationInFrames,
        videoStartFromInSeconds,
        isDraggingInTimeline: false,
        isMainTrack: true,
        renderMeta,
      };
    }

    const renderMeta = {
      clipType: CLIP_TYPES.IMAGE,
      id: clipId,
      linkId,
      start: 0,
      end: 0,
      videoStart: startMs,
      videoEnd: endMs,
    } as ImageItem['renderMeta'];

    return {
      ...template,
      id: `${template.id}-${generateRandomId(6)}`,
      from,
      durationInFrames,
      isDraggingInTimeline: false,
      isMainTrack: true,
      renderMeta,
    };
  });

  return createdItems.filter(Boolean) as Array<VideoItem | ImageItem>;
};

/** 오디오 시작 시점과 가장 가까운 템플릿 비디오/이미지를 선택 */
const pickTemplateVisualItem = ({
  templates,
  clipStartMs,
  fps,
}: {
  templates: Array<VideoItem | ImageItem>;
  clipStartMs: number;
  fps: number;
}) => {
  if (!templates.length) return null;
  /** 템플릿 선택 시 허용할 인접 오차 */
  const epsilonMs = convertFrameToMs(2, fps);
  const sortedTemplates = [...templates].sort((a, b) => a.from - b.from);

  const covering = sortedTemplates.find((item) => {
    const { startMs, endMs } = getMsRangeFromItem(item, fps);
    return (
      startMs - epsilonMs <= clipStartMs && endMs + epsilonMs >= clipStartMs
    );
  });

  if (covering) return covering;

  const before = sortedTemplates.filter((item) => {
    const { startMs } = getMsRangeFromItem(item, fps);
    return startMs <= clipStartMs;
  });

  if (before.length > 0) {
    return before[before.length - 1];
  }

  return sortedTemplates[0];
};

/** shiftItems에 해당하는 item들의 from과 renderMeta를 조정하는 함수 */
const shiftItem = ({
  shiftItemIds,
  finalizedItems,
  shiftFrom,
  shiftFromMs,
}: {
  shiftItemIds: string[];
  finalizedItems: Record<string, EditorStarterItem>;
  shiftFrom: number;
  shiftFromMs: number;
}) => {
  // 지정된 아이템들의 from/renderMeta 시간을 밀어준다.
  shiftItemIds.forEach((itemId) => {
    const item = finalizedItems[itemId];
    if (!item) return;

    const shiftedFrom = (item.from ?? 0) + shiftFrom;

    if (
      !isAudioItem(item) &&
      !isCaptionsItem(item) &&
      !isVideoItem(item) &&
      !isImageItem(item)
    )
      return;
    const renderMeta = item.renderMeta;
    const shiftedRenderMeta = renderMeta
      ? {
          ...renderMeta,
          videoStart:
            typeof renderMeta.videoStart === 'number'
              ? renderMeta.videoStart + shiftFromMs
              : renderMeta.videoStart,
          videoEnd:
            typeof renderMeta.videoEnd === 'number'
              ? renderMeta.videoEnd + shiftFromMs
              : renderMeta.videoEnd,
        }
      : renderMeta;

    finalizedItems[itemId] = {
      ...item,
      from: shiftedFrom,
      renderMeta: shiftedRenderMeta,
    } as typeof item;
  });
};

/** 주어진 프레임 이후 시작하는 아이템을 id 리스트로 수집 */
const collectItemsStartingAtOrAfterFrame = ({
  items,
  startFrame,
}: {
  items: Record<string, EditorStarterItem>;
  startFrame: number;
}) => {
  const thresholdFrame = Number.isFinite(startFrame) ? startFrame : 0;

  return Object.values(items)
    .filter(
      (item) =>
        typeof item?.id === 'string' &&
        (isAudioItem(item) ||
          isCaptionsItem(item) ||
          isVideoItem(item) ||
          isImageItem(item)) &&
        item.isMainTrack === true,
    )
    .filter((item) => (item.from ?? 0) >= thresholdFrame)
    .map((item) => item.id as string);
};

/** 연속 오디오 구간 사이의 ±1프레임 오차를 보정 */
const fixOneFrameOffsetBetweenSequentialAudioItems = ({
  items,
  fps,
}: {
  items: Record<string, EditorStarterItem>;
  fps: number;
}) => {
  const audioItems = Object.values(items).filter(
    (item): item is AudioItem => isAudioItem(item) && item.isMainTrack === true,
  );

  if (audioItems.length < 2) return;

  const sorted = audioItems.sort((a, b) => {
    const fromA = a.from ?? 0;
    const fromB = b.from ?? 0;

    if (fromA === fromB) {
      return a.id.localeCompare(b.id);
    }

    return fromA - fromB;
  });

  sorted.forEach((audioItem, index) => {
    const nextAudioItem = sorted[index + 1];
    if (!nextAudioItem) return;

    const currentId = audioItem.id;
    const nextId = nextAudioItem.id;
    if (!currentId || !nextId) return;

    const current = items[currentId] as AudioItem | undefined;
    const next = items[nextId] as AudioItem | undefined;
    if (!current || !next) return;

    const currentFrom = current.from ?? 0;
    const currentDuration = current.durationInFrames ?? 0;
    const nextFrom = next.from ?? 0;

    const diff = nextFrom - (currentFrom + currentDuration);

    if (diff === 0 || Math.abs(diff) !== 1) return;

    const shiftFrom = -diff;
    const shiftFromMs = convertFrameToMs(shiftFrom, fps);

    const shiftItemIds = collectItemsStartingAtOrAfterFrame({
      items,
      startFrame: nextFrom,
    });

    if (shiftItemIds.length === 0) return;

    shiftItem({
      shiftItemIds,
      finalizedItems: items,
      shiftFrom,
      shiftFromMs,
    });
  });
};

/** item의 from과 durationInFrames를 기준으로 ms로 변환하는 함수 */
const getItemEndMs = ({
  item,
  fps,
}: {
  item: AudioItem | CaptionsItem | VideoItem | ImageItem | null;
  fps: number;
}) => {
  if (!item) return null;

  // from + durationInFrames로 절대 종료 ms 계산
  const from = item.from ?? 0;
  const durationInFrames = item.durationInFrames ?? 0;

  return convertFrameToMs(from + durationInFrames, fps);
};

const getItemStartMs = ({
  item,
  fps,
}: {
  item: AudioItem | CaptionsItem | VideoItem | ImageItem | null;
  fps: number;
}) => {
  if (!item) return null;

  // from을 절대 ms로 변환
  return convertFrameToMs(item.from ?? 0, fps);
};

const getMsRangeFromItem = (
  item: EditorStarterItem | null,
  fps: number,
): { startMs: number; endMs: number } => {
  if (!item) {
    return { startMs: 0, endMs: 0 };
  }

  const startMs = convertFrameToMs(item.from ?? 0, fps);
  const endMs = convertFrameToMs(
    (item.from ?? 0) + (item.durationInFrames ?? 0),
    fps,
  );

  return { startMs, endMs };
};

/** ms 단위 구간을 Remotion 프레임 구간으로 변환 */
const msRangeToFrameRange = ({
  startMs,
  endMs,
  fps,
}: {
  startMs: number;
  endMs?: number | null;
  fps: number;
}): { from: number; durationInFrames: number } => {
  // ms 단위 구간을 Remotion 프레임 구간으로 변환
  const from = Math.max(0, Math.round((startMs / 1000) * fps));
  const hasEnd = typeof endMs === 'number';
  const endFrame = hasEnd ? Math.round(((endMs as number) / 1000) * fps) : null;
  const durationInFrames =
    endFrame !== null && endFrame > from ? endFrame - from : 1;

  return { from, durationInFrames };
};

/** 전달받은 existingLinkIds를 제외한 linkId를 생성하는 클로저 */
const createLinkIdGenerator = (existingLinkIds: number[]) => {
  const used = new Set(
    existingLinkIds.filter((id) => Number.isFinite(id) && id > 0),
  );
  let next = 1;
  while (used.has(next)) next += 1; // 초기 시작값을 비어 있는 곳으로 맞춤

  return () => {
    while (used.has(next)) next += 1; // 이미 사용된 id는 건너뜀
    const current = next;
    used.add(current);
    next += 1;
    return current;
  };
};

/** 호출 시점 기준으로 고유 clipId를 생성하는 헬퍼 */
const createClipIdGenerator = () => {
  let clipIdCounter = Date.now();
  return () => {
    clipIdCounter += 1;
    return clipIdCounter;
  };
};

/** items 전체에서 linkId가 있는 경우에 해당 linkId를 제외한 linkId를 전부 가져오는 함수 */
const getExistingItemLinkIds = (
  items: EditorStarterItem[],
  existingLinkIds: EditorStarterItem[],
) => {
  const excludedLinkIds = new Set(
    items
      .filter((item) => isAudioItem(item) || isCaptionsItem(item))
      .map((item) => item.renderMeta?.linkId)
      .filter((id): id is number => id !== undefined),
  );

  existingLinkIds.forEach((item) => {
    if (
      (isAudioItem(item) || isCaptionsItem(item)) &&
      item.renderMeta?.linkId
    ) {
      excludedLinkIds.delete(item.renderMeta.linkId);
    }
  });

  return Array.from(excludedLinkIds ?? []).sort((a, b) => a - b);
};

/** audio clip을 순회하며 videoStart/videoEnd를 절대 ms로 맞추기 위한 함수 */
const normalizeAudioAndCaptionClipsSequentially = ({
  ttsAudioClips,
  ttsCaptionClips,
  originalStartCaptionItem,
  sceneId,
  fps,
  getNextClipId,
  getNextLinkId,
}: {
  ttsAudioClips: ClipInfo[];
  ttsCaptionClips: ClipInfo[];
  originalStartCaptionItem: CaptionsItem | null;
  sceneId: number;
  fps: number;
  getNextClipId: () => number;
  getNextLinkId: () => number;
}): {
  normalizedAudioClips: AudioClipInfo[];
  normalizedAudioClipMapByLinkId: Map<number, AudioClipInfo>;
  normalizedCaptionClips: CaptionClipInfo[];
} => {
  let currentStartMs =
    convertFrameToMs(originalStartCaptionItem?.from ?? 0, fps) ?? 0;

  const normalizedCaptionClips: CaptionClipInfo[] = [];

  const normalizedAudioClips = ttsAudioClips
    .filter(isAudioClip)
    .map((clip, idx) => {
      const { start = 0, end = 0 } = clip;

      const duration = end - start;
      const videoStart = currentStartMs;
      const videoEnd = videoStart + duration;
      currentStartMs = videoEnd;
      const clipId = (clip?.id ?? 0) > 0 ? clip.id : getNextClipId();

      const linkId = getNextLinkId();

      const baseCaptionClip = ttsCaptionClips[idx];
      if (baseCaptionClip) {
        const captionClip = {
          ...baseCaptionClip,
          videoStart,
          videoEnd,
          sceneId,
          id: clipId,
          linkId,
        };
        normalizedCaptionClips.push(captionClip as CaptionClipInfo);
      }

      return {
        ...clip,
        sceneId,
        videoStart,
        videoEnd,
        id: clipId,
        linkId,
      };
    });

  const normalizedAudioClipMapByLinkId = new Map<number, AudioClipInfo>();
  normalizedAudioClips.forEach((clip) => {
    if (isAudioClip(clip) && clip?.linkId) {
      normalizedAudioClipMapByLinkId.set(clip.linkId, clip);
    }
  });
  return {
    normalizedAudioClips,
    normalizedAudioClipMapByLinkId,
    normalizedCaptionClips,
  };
};

/** 캡션 아이템의 스타일만 포함한 객체를 추출 (위치/크기 정보 제외) */
const getCaptionStyle = (captionItem?: CaptionsItem | null) => {
  if (!captionItem) {
    return null;
  }
  // 타임라인/메타데이터 및 위치/크기 관련 속성 제외
  // width, height, left, top은 새 텍스트에 맞게 재계산되어야 함
  // captionSyncLeadMs와 captionTimingPolicyVersion은 클립별 타이밍 메타데이터라 스타일이 아님
  const excludedKeys: (keyof CaptionsItem)[] = [
    'assetId',
    'captionStartInSeconds',
    'captionSyncLeadMs',
    'captionTimingPolicyVersion',
    'durationInFrames',
    'fadeInDurationInSeconds',
    'fadeOutDurationInSeconds',
    'from',
    'id',
    'pageDurationInMilliseconds',
    'renderMeta',
    'maxLines',
    'width',
    'height',
    'left',
    'top',
  ];

  return omit(captionItem, excludedKeys);
};

/** 현재 씬의 items의 id 또는 assetId를 전부 가져오는 함수 (audio/caption) */
const getCurrentSceneIds = (
  editorItems: EditorStarterItem[],
  key: 'id' | 'assetId',
) =>
  editorItems
    .filter((item) => item.type === 'audio' || item.type === 'captions')
    .map((item) => item[key]);

/** 현재 씬의 caption 의 from시작과 끝을 찾는 함수 */
const getSceneCaptionFromRange = (
  currentSceneCaptionItems: CaptionsItem[],
): {
  startCaptionItem: CaptionsItem | null;
  endCaptionItem: CaptionsItem | null;
} => {
  const { startCaptionItem, endCaptionItem } = currentSceneCaptionItems.reduce(
    (state, item) => {
      const from = item.from ?? 0;
      const end = from + item.durationInFrames;

      const isEarlier = from < state.startFrom;
      const isLater = end > state.endFrame;

      return {
        startFrom: isEarlier ? from : state.startFrom,
        endFrame: isLater ? end : state.endFrame,
        startCaptionItem: isEarlier ? item : state.startCaptionItem,
        endCaptionItem: isLater ? item : state.endCaptionItem,
      };
    },
    {
      startFrom: Number.POSITIVE_INFINITY,
      endFrame: Number.NEGATIVE_INFINITY,
      startCaptionItem: null as CaptionsItem | null,
      endCaptionItem: null as CaptionsItem | null,
    },
  );

  return { startCaptionItem, endCaptionItem };
};

/** 서버에서 받은 TTS 클립을 오디오/캡션 배열로 분리 */
const splitTtsAudioAndCaptionClips = (tts: ClipInfo[]) => {
  const ttsAudioResponseClips = tts.filter(isAudioClip);
  const ttsCaptionResponseClips = tts.filter(isCaptionClip);
  return {
    ttsAudioResponseClips,
    ttsCaptionResponseClips,
  };
};

/** 기존 캡션에서 TextStyleConfig를 생성 (captionYRatio 포함) */
const createTextStyleConfigFromCaption = (
  captionItem: CaptionsItem | null,
  compositionHeight: number,
): TextStyleConfig | undefined => {
  if (!captionItem) {
    return undefined;
  }

  // 기존 캡션의 top, height에서 yRatio를 역산
  // centerY = top + height / 2
  // yRatio = centerY / compositionHeight
  const centerY = captionItem.top + captionItem.height / 2;
  const captionYRatio =
    compositionHeight > 0 ? centerY / compositionHeight : 0.75;

  return {
    captionFontFamily: captionItem.fontFamily,
    captionFontSize: captionItem.fontSize,
    captionLineHeight: captionItem.lineHeight,
    captionLetterSpacing: captionItem.letterSpacing,
    captionColor: captionItem.color,
    captionStrokeWidth: captionItem.strokeWidth,
    captionStrokeColor: captionItem.strokeColor,
    captionYRatio,
  };
};
